<template>
  <div>
    <Head title="Notificări" />
    <div class="mb-8 flex items-center justify-between gap-4">
      <h1 class="text-3xl font-bold">Notificări</h1>
      <button
        v-if="unreadCount > 0"
        class="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
        @click="markAllAsRead"
      >
        Marchează toate ca citite
      </button>
      <button
        v-if="localNotifications.length > 0"
        class="text-sm text-red-500 hover:text-red-700 cursor-pointer"
        @click="deleteAllNotifications"
      >
        Șterge toate
      </button>
    </div>

    <div v-if="dateGroups.length === 0" class="text-gray-500">
      Nu există notificări
    </div>

    <div v-for="group in dateGroups" :key="group.key" class="mb-8">
      <h2 class="mb-3 text-sm font-semibold text-gray-500">
        {{ group.label }}
      </h2>

      <div class="overflow-hidden rounded-md bg-white shadow">
        <div
          v-for="notification in group.items"
          :key="notification.id"
          class="flex items-stretch border-b border-gray-100"
        >
          <div
            class="flex-1 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            :class="{ 'bg-blue-50': !notification.seen }"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <p class="text-sm text-gray-800">
                  <template v-if="isFeedbackFile(notification)">
                    <span class="font-semibold">{{
                      feedbackParts(notification).who
                    }}</span>
                    a încărcat {{ filesCountLabel(notification) }} la
                    feedback-ul
                    <span class="font-semibold">{{
                      feedbackParts(notification).subject
                    }}</span>
                    (
                    <span class="font-semibold">{{
                      feedbackParts(notification).course
                    }}</span>
                    ).
                  </template>
                  <template v-else-if="isTestPassed(notification)">
                    <span class="font-semibold">{{
                      testPassedParts(notification).who
                    }}</span>
                    a finalizat evaluarea
                    <span class="font-semibold">{{
                      testPassedParts(notification).subject
                    }}</span>
                    din cursul
                    <span class="font-semibold">{{
                      testPassedParts(notification).course
                    }}</span>
                    cu scorul de
                    <span
                      :class="
                        testPassedParts(notification).result >= 75
                          ? 'font-bold text-green-600'
                          : 'font-bold text-red-600'
                      "
                      >{{ testPassedParts(notification).result }}%</span
                    >.
                  </template>
                  <template v-else-if="isExamAllocated(notification)">
                    Ti-a fost alocat un examen:
                    <span class="font-semibold">{{
                      notification.subject_name
                    }}</span>
                    (<span class="font-semibold">{{
                      notification.course_name
                    }}</span
                    >).
                  </template>
                  <template v-else-if="isUploadLimitExceeded(notification)">
                    {{ notification?.trigger_user_name || 'Un utilizator' }} a
                    încercat să încarce mai mult decat 15 fișiere in 24h.
                  </template>
                  <template v-else>
                    {{ fallbackMessage(notification) }}
                  </template>
                </p>
                <p class="mt-1 text-xs text-gray-500">
                  {{ formatDate(notification.created_at) }}
                </p>
              </div>
              <i
                v-if="!notification.seen"
                class="pi pi-circle-fill text-blue-500 text-xs mt-1 shrink-0"
              ></i>
            </div>
          </div>
          <button
            class="px-3 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors border-l border-gray-100 cursor-pointer"
            title="Șterge notificarea"
            @click="deleteOne(notification.id)"
          >
            <i class="pi pi-trash text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import * as timeago from 'timeago.js';
import notificationsService from '@/services/notificationsService';
import { quizResultPercentage } from '@/utils/formatters';

export default {
  components: {
    Head,
  },
  layout: Layout,
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      notificationsState: notificationsService.state,
    };
  },
  computed: {
    localNotifications() {
      return this.notificationsState.notifications;
    },
    unreadCount() {
      return notificationsService.unreadCount.value;
    },
    dateGroups() {
      const groups = new Map();

      for (const notification of this.localNotifications) {
        const date = new Date(notification.created_at);
        const dayKey = date.toISOString().slice(0, 10); // YYYY-MM-DD

        if (!groups.has(dayKey)) {
          groups.set(dayKey, []);
        }

        groups.get(dayKey).push(notification);
      }

      return Array.from(groups.entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([key, items]) => {
          const label = new Date(`${key}T00:00:00`).toLocaleDateString(
            undefined,
            {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            },
          );

          return { key, label, items };
        });
    },
  },
  mounted() {
    notificationsService.hydrate(this.notifications);
    notificationsService.fetchNotifications();
  },
  methods: {
    formatDate(date) {
      return timeago.format(date);
    },
    async markAllAsRead() {
      try {
        await notificationsService.markAllAsSeen();
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    },
    async deleteOne(notificationId) {
      try {
        await notificationsService.deleteNotification(notificationId);
      } catch (error) {
        console.error('Error deleting notification:', error);
      }
    },
    async deleteAllNotifications() {
      if (
        !confirm(
          'Ești sigur(ă) că vrei să ștergi toate notificările? Această acțiune nu poate fi anulată.',
        )
      )
        return;
      try {
        await notificationsService.deleteAll();
      } catch (error) {
        console.error('Error deleting all notifications:', error);
      }
    },
    async handleNotificationClick(notification) {
      if (!notification.seen) {
        try {
          await notificationsService.markAsSeen(notification.id);
        } catch (error) {
          console.error('Error marking notification as seen:', error);
        }
      }

      if (notification.content_type === 'FeedbackFile') {
        const feedbackId = notification?.content?.feedback_id;
        if (feedbackId) this.$inertia.visit(this.$routes.feedback(feedbackId));
      } else if (notification.content_type === 'TestPassed') {
        // Navigate to quiz results page
        this.$inertia.visit(this.$routes.quiz_results());
      } else if (notification.content_type === 'ExamAllocated') {
        if (notification.subject_id) {
          this.$inertia.visit(this.$routes.subject(notification.subject_id));
        }
      }
    },
    isFeedbackFile(notification) {
      return notification?.content_type === 'FeedbackFile';
    },
    isTestPassed(notification) {
      return notification?.content_type === 'TestPassed';
    },
    isExamAllocated(notification) {
      return notification?.content_type === 'ExamAllocated';
    },
    isUploadLimitExceeded(notification) {
      return notification?.content_type === 'UploadLimitExceeded';
    },
    feedbackParts(notification) {
      return {
        who: notification?.trigger_user_name || 'Un utilizator',
        subject: notification?.subject_name || 'un feedback',
        course: notification?.course_name || 'un curs',
      };
    },
    filesCountLabel(notification) {
      const count = notification?.content?.files_count;
      if (typeof count !== 'number') return 'fișiere';
      if (count === 1) return 'un fișier';
      return `${count} fișiere`;
    },
    testPassedParts(notification) {
      return {
        who: notification?.trigger_user_name || 'Un utilizator',
        subject: notification?.subject_name || 'o evaluare',
        course: notification?.course_name || 'un curs',
        result: quizResultPercentage(
          notification?.correct_answers_count ?? 0,
          notification?.questions_count ?? 0,
        ),
      };
    },
    fallbackMessage(notification) {
      if (typeof notification?.content === 'string')
        return notification.content;
      return 'Notificare';
    },
  },
};
</script>
